import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import authService from './authService';

interface TokenRefreshResponse {
    access: string;
}

class TokenRefreshService {
    private isRefreshing = false;
    private failedQueue: Array<{
        resolve: (value?: any) => void;
        reject: (error?: any) => void;
    }> = [];

    private processQueue(error: any, token: string | null = null) {
        this.failedQueue.forEach(({ resolve, reject }) => {
            if (error) {
                reject(error);
            } else {
                resolve(token);
            }
        });
        this.failedQueue = [];
    }

    async refreshToken(): Promise<string> {
        const refreshToken = authService.getRefreshToken();
        if (!refreshToken) {
            throw new Error('No refresh token available');
        }

        try {
            // Use direct axios call to avoid infinite loop with api interceptor
            const response = await axios.post<TokenRefreshResponse>(
                `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api'}/auth/token/refresh/`,
                { refresh: refreshToken },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    timeout: 10000, // 10 second timeout for refresh requests
                }
            );

            const { access } = response.data;
            authService.setAccessToken(access);
            return access;
        } catch (error) {
            // If refresh token is also invalid, clear all tokens
            authService.clearTokens();
            throw error;
        }
    }

    async handleTokenRefresh(error: AxiosError): Promise<AxiosResponse> {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        if (error.response?.status === 401 && !originalRequest._retry) {
            if (this.isRefreshing) {
                // If already refreshing, queue this request
                return new Promise((resolve, reject) => {
                    this.failedQueue.push({ resolve, reject });
                }).then((token) => {
                    if (originalRequest.headers) {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                    }
                    return axios(originalRequest);
                }).catch((err) => {
                    return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            this.isRefreshing = true;

            try {
                const newToken = await this.refreshToken();
                this.processQueue(null, newToken);

                // Retry the original request with new token
                if (originalRequest.headers) {
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                }
                return axios(originalRequest);
            } catch (refreshError) {
                this.processQueue(refreshError, null);
                return Promise.reject(refreshError);
            } finally {
                this.isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
}

export default new TokenRefreshService(); 